import React from 'react';
import { useTranslation } from 'react-i18next';
import { Race } from './hooks';

interface Props {
    races: Race[];
    onEdit: (race: Race) => void;
    onDelete: (id: string) => void;
    deletingId: string | null;
}

const PastRacesSection: React.FC<Props> = ({ races, onEdit, onDelete, deletingId }) => {
    const { t } = useTranslation();
    const formatDate = (d: string) =>
        new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });

    if (races.length === 0) {
        return (
            <p className="text-sm text-slate-400 italic text-center py-8">
                {t('races.noPast')}
            </p>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {races.map((race) => (
                <div key={race.id} className="group relative p-4 border border-cyan-100 rounded-xl hover:shadow-md hover:shadow-cyan-100 transition-shadow bg-white">

                    {/* Edit / Delete overlay */}
                    <div className="absolute top-3 right-3 flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <button
                            onClick={() => onEdit(race)}
                            className="w-7 h-7 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 flex items-center justify-center transition-colors"
                            title={t('common.edit')}
                        >
                            <i className="fa-solid fa-pen-to-square text-xs"></i>
                        </button>
                        <button
                            onClick={() => onDelete(race.id)}
                            disabled={deletingId === race.id}
                            className="w-7 h-7 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors disabled:opacity-40"
                            title={t('common.delete')}
                        >
                            {deletingId === race.id
                                ? <i className="fa-solid fa-spinner fa-spin text-xs"></i>
                                : <i className="fa-solid fa-trash-can text-xs"></i>
                            }
                        </button>
                    </div>

                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3 pr-16">
                        <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-500 flex items-center justify-center flex-shrink-0">
                            <i className="fa-solid fa-flag-checkered"></i>
                        </div>
                        <div className="min-w-0">
                            <h4 className="font-bold text-slate-800 text-sm truncate">{race.raceType}</h4>
                            <p className="text-xs text-slate-500">{formatDate(race.date)}</p>
                        </div>
                    </div>

                    {/* Location */}
                    <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                        <i className="fa-solid fa-location-dot text-slate-400"></i>
                        {race.location}
                    </p>

                    {/* Results */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {race.finishTime && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded text-xs font-mono font-medium">
                                <i className="fa-solid fa-stopwatch text-slate-400 text-[10px]"></i>
                                {race.finishTime}
                            </span>
                        )}
                        {race.position && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                <i className="fa-solid fa-trophy text-[10px]"></i>
                                {race.position}
                            </span>
                        )}
                        {race.achievement && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs font-bold">
                                <i className="fa-solid fa-medal text-[10px]"></i>
                                {race.achievement}
                            </span>
                        )}
                    </div>

                    {race.notes && (
                        <p className="mt-2 text-xs text-slate-500 italic truncate">{race.notes}</p>
                    )}
                </div>
            ))}
        </div>
    );
};

export default PastRacesSection;
